import {
  CampaignActivityLogDTO,
  CampaignActivityLogType,
  CampaignMaterialActivityLogDTO,
  CampaignMaterialActivityLogType,
} from "modules/Campaigns/service/campaigns.types";
import moment from "moment";
import { Container, Grid, Tooltip, Typography, Box } from "@mui/material";
import React, { useState } from "react";

const safeParseJSON = (
  jsonString: string | null
): Record<string, any> | null => {
  if (!jsonString) return null;

  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return null;
  }
};

const ValueChanges = ({
  oldValues,
  newValues,
}: {
  oldValues: string | null;
  newValues: string | null;
}) => {
  const oldObj = safeParseJSON(oldValues);
  const newObj = safeParseJSON(newValues);

  if (!oldObj && !newObj) return null;

  // Get all unique keys from both objects
  const allKeys = new Set([
    ...(oldObj ? Object.keys(oldObj) : []),
    ...(newObj ? Object.keys(newObj) : []),
  ]);

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      {Array.from(allKeys).map((key) => {
        const oldValue = oldObj?.[key] !== undefined ? String(oldObj[key]) : "";
        const newValue = newObj?.[key] !== undefined ? String(newObj[key]) : "";

        if (
          oldValue !== newValue ||
          (!oldValue && newValue) ||
          (oldValue && !newValue)
        ) {
          return (
            <Typography variant="body2" key={key} sx={{ fontSize: "0.85rem" }}>
              <strong>{key}:</strong> {oldValue ? `"${oldValue}"` : "(empty)"} →{" "}
              {newValue ? `"${newValue}"` : "(empty)"}
            </Typography>
          );
        }
        return null;
      })}
    </Box>
  );
};

export const ActivityLogInitiatorUserTooltip = ({
  email,
  name,
}: {
  email: string;
  name: string;
}) => {
  const [tooltip, setTooltip] = useState(email);

  const onClick = () => {
    // eslint-disable-next-line no-undef
    navigator.clipboard.writeText(email);
    setTooltip("Copied to clipboard");

    setTimeout(() => {
      setTooltip(email);
    }, 1500);
  };

  return (
    <Tooltip arrow title={tooltip}>
      <strong onClick={onClick}>{name}</strong>
    </Tooltip>
  );
};

export const CampaignActivityLog = ({
  entries,
}: {
  entries: Array<CampaignActivityLogDTO | CampaignMaterialActivityLogDTO>;
}) => {
  return (
    <Container sx={{ p: 4 }}>
      {entries
        .sort((a, b) =>
          moment.utc(a.createdOn).isAfter(moment.utc(b.createdOn)) ? -1 : 1
        )
        .map((logEntry, index) => {
          // Type guard to determine the kind of log entry
          const isCampaignLog = "orderWindowId" in logEntry;
          const isMaterialLog = "orderWindowMaterialId" in logEntry;

          return (
            <Grid
              sx={{ mb: 2 }}
              spacing={4}
              container
              justifyContent={"space-between"}
              key={index}
            >
              <Grid item xs>
                <div>
                  {logEntry.createdBy && (
                    <ActivityLogInitiatorUserTooltip
                      name={logEntry.createdBy
                        .split("@")[0]
                        .split(".")
                        .join(" ")}
                      email={logEntry.createdBy}
                    />
                  )}
                  {!logEntry.createdBy && (
                    <span className="pr-1 italic">System</span>
                  )}
                  <span>
                    {" "}
                    {isCampaignLog &&
                      {
                        [CampaignActivityLogType.OrderWindowCreated]:
                          "created campaign",
                        [CampaignActivityLogType.OrderWindowUpdated]:
                          "updated campaign",
                        [CampaignActivityLogType.OrderWindowDeleted]:
                          "deleted campaign",
                        [CampaignActivityLogType.OrderWindowSubscriberAdded]:
                          "added subscriber",
                        [CampaignActivityLogType.OrderWindowSubscriberRemoved]:
                          "removed subscriber",
                        [CampaignActivityLogType.OrderWindowPublished]:
                          "published campaign",
                        [CampaignActivityLogType.OrderWindowClosed]:
                          "closed campaign",
                        [CampaignActivityLogType.OrderWindowAttachmentUploaded]:
                          "uploaded attachment",
                        [CampaignActivityLogType.OrderWindowAttachmentDeleted]:
                          "deleted attachment",
                      }[logEntry.type]}
                    {isMaterialLog &&
                      {
                        [CampaignMaterialActivityLogType.OrderWindowMaterialCreated]: `created material #${
                          (logEntry as CampaignMaterialActivityLogDTO)
                            .orderWindowMaterialId
                        }`,
                        [CampaignMaterialActivityLogType.OrderWindowMaterialUpdated]: `updated material #${
                          (logEntry as CampaignMaterialActivityLogDTO)
                            .orderWindowMaterialId
                        }`,
                        [CampaignMaterialActivityLogType.OrderWindowMaterialDeleted]: `deleted material #${
                          (logEntry as CampaignMaterialActivityLogDTO)
                            .orderWindowMaterialId
                        }`,
                        [CampaignMaterialActivityLogType.OrderWindowMaterialQuantityRequested]: `requested quantity for material #${
                          (logEntry as CampaignMaterialActivityLogDTO)
                            .orderWindowMaterialId
                        }`,
                        [CampaignMaterialActivityLogType.OrderWindowMaterialQuantityUpdated]: `updated quantity request for material #${
                          (logEntry as CampaignMaterialActivityLogDTO)
                            .orderWindowMaterialId
                        }`,
                        [CampaignMaterialActivityLogType.OrderWindowMaterialQuantityDeleted]: `deleted quantity request for material #${
                          (logEntry as CampaignMaterialActivityLogDTO)
                            .orderWindowMaterialId
                        }`,
                      }[logEntry.type]}
                  </span>
                </div>
                {logEntry.additionalInformation && (
                  <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                    {logEntry.additionalInformation}
                  </Typography>
                )}
                <ValueChanges
                  oldValues={logEntry.oldValues}
                  newValues={logEntry.newValues}
                />
              </Grid>

              <Grid item>
                <Tooltip
                  arrow
                  title={moment(logEntry.createdOn).format("DD/MM/YYYY h:mm a")}
                >
                  <span>{moment.utc(logEntry.createdOn).fromNow()}</span>
                </Tooltip>{" "}
              </Grid>
            </Grid>
          );
        })}
    </Container>
  );
};

export default CampaignActivityLog;
